import type { FilmProject } from "../types";
import type { MissingInput } from "../utils/analysisSummary";
import { missingInputsForAnalysis, reviewGroupsForAnalysis, sceneReadinessCounts } from "../utils/analysisSummary";
import { Icon } from "./Icon";

interface AIParseReviewProps {
  analysis: Record<string, unknown>;
  project: FilmProject;
  isAnalyzing: boolean;
  onApprove: () => void;
  onBack: () => void;
  onRerun: () => void;
  onResolveMissingInput: (item: MissingInput) => void;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
}

export function AIParseReview({ analysis, project, isAnalyzing, onApprove, onBack, onRerun, onResolveMissingInput }: AIParseReviewProps) {
  const groups = reviewGroupsForAnalysis(analysis, project);
  const missingInputs = missingInputsForAnalysis(analysis);
  const readiness = sceneReadinessCounts(project.scenes);
  const parseWarnings = stringList(analysis.warnings).slice(0, 3);
  const parseNotices = stringList(analysis.notices).slice(0, 3);

  return (
    <div className="flow-view parse-view">
      <header className="flow-hero">
        <div>
          <span>AI Parse Review</span>
          <h1>Check what the script analysis found.</h1>
          <p>Approve the parsed scenes, characters, locations, voices, and visual direction before Kinetix builds the production plan.</p>
        </div>
        <div className="flow-actions">
          <button className="secondary-action" onClick={onBack} type="button">
            Back
          </button>
          <button className="secondary-action" disabled={isAnalyzing} onClick={onRerun} type="button">
            <Icon name="spark" />
            {isAnalyzing ? "Re-running" : "Improve Parse"}
          </button>
          <button className="primary-action" onClick={onApprove} type="button">
            Approve & Continue
          </button>
        </div>
      </header>

      {parseWarnings.length || parseNotices.length ? (
        <section className="parse-source-messages" aria-label="Analysis source notes">
          {parseWarnings.map((warning) => (
            <article className="warning" key={`warning-${warning}`}>
              <Icon name="script" />
              <div>
                <strong>Review note</strong>
                <p>{warning}</p>
              </div>
            </article>
          ))}
          {parseNotices.map((notice) => (
            <article className="notice" key={`notice-${notice}`}>
              <Icon name="spark" />
              <div>
                <strong>Source note</strong>
                <p>{notice}</p>
              </div>
            </article>
          ))}
        </section>
      ) : null}

      <section className="parse-review-grid">
        <article className="setup-card review-overview-card">
          <div className="card-heading">
            <span>Parse Summary</span>
            <h2>{project.scenes.length} scenes ready for review</h2>
          </div>
          <div className="readiness-grid compact-readiness">
            <div>
              <span>Ready</span>
              <strong>{readiness.ready}</strong>
            </div>
            <div>
              <span>Need voice</span>
              <strong>{readiness.needsVoice}</strong>
            </div>
            <div>
              <span>Need card</span>
              <strong>{readiness.needsCharacter}</strong>
            </div>
            <div>
              <span>Need ref</span>
              <strong>{readiness.missingReference}</strong>
            </div>
          </div>
          <div className="review-group-grid">
            {groups.map((group) => (
              <article className={`parse-card ${group.status}`} key={group.id}>
                <span>{group.badge}</span>
                <strong>{group.title}</strong>
                <small>{group.countLabel}</small>
                <p>{group.detail}</p>
              </article>
            ))}
          </div>
        </article>

        <aside className="setup-card improve-panel">
          <div className="card-heading">
            <span>Fix Only What Is Flagged</span>
            <h2>Missing inputs</h2>
          </div>
          {missingInputs.length ? (
            <div className="missing-input-list">
              {missingInputs.map((item) => (
                <div key={item.id}>
                  <span className={item.required ? "required-dot" : "optional-dot"} />
                  <div>
                    <strong>{item.label}</strong>
                    <small>{item.kind}</small>
                  </div>
                  <button className="secondary-action" onClick={() => onResolveMissingInput(item)} type="button">Resolve</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-copy">No required assets are flagged. You can still add optional references in the storyboard workspace.</p>
          )}
          <details className="advanced-drawer">
            <summary>Parsed payload</summary>
            <div>
              <span>This is the reviewed AI analysis object before project commit.</span>
              <pre>{JSON.stringify(analysis, null, 2).slice(0, 5000)}</pre>
            </div>
          </details>
        </aside>
      </section>
    </div>
  );
}
